import { Extension } from '@tiptap/core'
import { Color } from '@tiptap/extension-color'
import { FontFamily } from '@tiptap/extension-font-family'
import { FontSize } from '@tiptap/extension-font-size'
import { TextStyle } from '@tiptap/extension-text-style'

/**
 * TextColor Extension - Provides text color functionality
 */
export const TextColor = Extension.create({
    name: 'textColor',

    addExtensions() {
        return [
            TextStyle.configure(),
            Color.configure()
        ]
    },
})

/**
 * TypographyExtension - Provides font size and font family functionality
 */
export const TypographyExtension = Extension.create({
    name: 'typography',

    addExtensions() {
        return [
            FontSize.configure({
                types: ['textStyle'],
            }),
            FontFamily.configure({
                types: ['textStyle'],
            }),
        ]
    },
})

/**
 * Sets text color and updates toolbar state
 * @param {Editor} editor - TipTap editor instance
 * @param {string} color - Color value (hex, rgb, etc.)
 * @returns {boolean} - Success status
 */
export const setTextColor = (editor, color) => {
    if (!editor || !color) return false

    // Set the color on the selected text
    const result = editor.chain().focus().setColor(color).run()

    // Return the color value for immediate toolbar update
    return result ? color : false
}


/**
 * Gets all text formatting attributes from current selection
 * @param {Editor} editor - TipTap editor instance
 * @returns {Object} - Object with color, fontSize, and fontFamily values
 */
export const getFormattingAttributes = (editor) => {
    if (!editor) return { color: '#000000', fontSize: '16px', fontFamily: 'Arial, sans-serif' }

    try {
        let attributes = { color: '#000000', fontSize: '16px', fontFamily: 'Arial, sans-serif' }

        const { empty } = editor.state.selection
        const $from = editor.state.selection.$from
        let textStyleMark = null

        if (!empty) {
            // Range selection: nodeAfter is the first character inside the selection,
            // which already has the newly applied mark (unlike $from.marks() which reads char BEFORE from)
            const nodeAfter = $from.nodeAfter
            if (nodeAfter && nodeAfter.isText) {
                textStyleMark = nodeAfter.marks.find(m => m.type.name === 'textStyle')
            }
        }

        if (!textStyleMark) {
            // Cursor: $from.marks() returns marks of adjacent text
            textStyleMark = $from.marks().find(m => m.type.name === 'textStyle')
        }

        if (textStyleMark) {
            if (textStyleMark.attrs.color) attributes.color = textStyleMark.attrs.color
            if (textStyleMark.attrs.fontSize) attributes.fontSize = textStyleMark.attrs.fontSize
            if (textStyleMark.attrs.fontFamily) attributes.fontFamily = textStyleMark.attrs.fontFamily
        }

        // storedMarks take priority — pending marks at cursor position (e.g. after A+/A- with no selection)
        const storedMarks = editor.state.storedMarks
        if (storedMarks) {
            const storedTextStyle = storedMarks.find(mark => mark.type.name === 'textStyle')
            if (storedTextStyle) {
                if (storedTextStyle.attrs.color) attributes.color = storedTextStyle.attrs.color
                if (storedTextStyle.attrs.fontSize) attributes.fontSize = storedTextStyle.attrs.fontSize
                if (storedTextStyle.attrs.fontFamily) attributes.fontFamily = storedTextStyle.attrs.fontFamily
            }
        }

        return attributes
    } catch (err) {
        console.error('Error getting text formatting attributes:', err)
        return { color: '#000000', fontSize: '16px', fontFamily: 'Arial, sans-serif' }
    }
}

/**
 * Gets current text color from selection
 * @param {Editor} editor - TipTap editor instance
 * @returns {string} - Current text color
 */
export const getCurrentTextColor = (editor) => {
    if (!editor) return '#000000'
    return getFormattingAttributes(editor).color
}



/**
 * Sets up event listeners for text formatting detection and updates
 * @param {Editor} editor - TipTap editor instance
 * @param {Object} setters - Object with setter functions for state updates
 * @returns {Function} - Cleanup function to remove event listeners
 */
export const setupFormattingDetection = (editor, setters) => {
    if (!editor || !setters) return null

    const { setCurrentTextColor, setCurrentFontSize, setCurrentFontFamily } = setters

    const updateFormattingFromSelection = () => {
        try {
            const attrs = getFormattingAttributes(editor)
            
            // Update state only if setter is provided
            if (setCurrentTextColor) setCurrentTextColor(attrs.color)
            if (setCurrentFontSize) setCurrentFontSize(attrs.fontSize)
            if (setCurrentFontFamily) setCurrentFontFamily(attrs.fontFamily)
        } catch (err) {
            console.error('Error updating formatting from selection:', err)
        }
    }

    editor.on('selectionUpdate', updateFormattingFromSelection)
    editor.on('update', updateFormattingFromSelection)
    editor.on('transaction', updateFormattingFromSelection)

    // Initial update
    updateFormattingFromSelection()

    // Return cleanup function
    return () => {
        editor.off('selectionUpdate', updateFormattingFromSelection)
        editor.off('update', updateFormattingFromSelection)
        editor.off('transaction', updateFormattingFromSelection)
    }
}